// FILE: src/components/admin/AuditLogBrowser.jsx
// Filters + entries of the audit log (WP-7, B-9, matrix 5.1 / 5.8), used by
// the audit-log page (every account) and a student's Activity page (one
// account, `userId`). The server filters and pages: the filters and the page
// live in the URL (?email=&action=&outcome=&from=&to=&page=&limit=), "Apply
// filters" sends them and goes back to page 1. `action` is one action
// (LOGIN_FAILURE) or a kind of event (group:sign-in) sent as a comma list.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Alert, Box, Button, LinearProgress, ListSubheader, MenuItem, Stack, TextField, Typography } from "@mui/material";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { auditQuery, fetchAuditLogs } from "../../features/auditLogsSlice";
import { EmptyState, ErrorState, LoadingState, Section } from "../ui";
import AuditLogTable, { ServerPagination } from "./AuditLogTable";
import { AUDIT_ACTIONS, AUDIT_GROUPS, OUTCOMES, actionsForFilter, isActionFilter } from "./auditActions";
import { dayOrEmpty, rangeProblem } from "./dates";
import { formatNumber } from "./format";
import { auditOutcomeLabel } from "../../i18n/labels";

const LIMITS = [25, 50, 100];
const ID_RE = /^[a-f0-9]{24}$/i;
const EMPTY = { email: "", action: "", outcome: "", from: "", to: "" };

function positiveInt(value, fallback) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/** The filters in the URL, cleaned. */
export function auditParams(params) {
  const action = params.get("action") || "";
  const outcome = params.get("outcome") || "";
  const limit = positiveInt(params.get("limit"), LIMITS[0]);
  return {
    email: (params.get("email") || "").slice(0, 254),
    action: isActionFilter(action) ? action : "",
    outcome: OUTCOMES.includes(outcome) ? outcome : "",
    from: dayOrEmpty(params.get("from")),
    to: dayOrEmpty(params.get("to")),
    userId: ID_RE.test(params.get("userId") || "") ? params.get("userId") : "",
    page: positiveInt(params.get("page"), 1),
    limit: LIMITS.includes(limit) ? limit : LIMITS[0]
  };
}

/**
 * userId: fixed account (a student's Activity page). showEmail: the
 * account-email filter. showAccount: the Account column.
 */
export default function AuditLogBrowser({ userId: fixedUserId, showEmail = true, showAccount = true, tableLabel = "Audit log entries" }) {
  const dispatch = useDispatch();
  const [params, setParams] = useSearchParams();
  const applied = auditParams(params);
  const userId = fixedUserId || applied.userId;
  const [draft, setDraft] = useState(EMPTY);
  const [problem, setProblem] = useState("");

  // Back/Forward: show the filters that are in the URL.
  useEffect(() => {
    setDraft({ email: applied.email, action: applied.action, outcome: applied.outcome, from: applied.from, to: applied.to });
  }, [applied.email, applied.action, applied.outcome, applied.from, applied.to]);

  const filters = useMemo(
    () => ({
      userId: userId || undefined,
      email: showEmail ? applied.email.trim() || undefined : undefined,
      action: actionsForFilter(applied.action) || undefined,
      outcome: applied.outcome || undefined,
      from: applied.from || undefined,
      to: applied.to || undefined,
      page: applied.page,
      limit: applied.limit
    }),
    [userId, showEmail, applied.email, applied.action, applied.outcome, applied.from, applied.to, applied.page, applied.limit]
  );
  const query = auditQuery(filters);

  const load = useCallback(() => dispatch(fetchAuditLogs(filters)), [dispatch, filters]);
  useEffect(() => {
    load();
  }, [load]);

  const state = useSelector((s) => s.auditLogs);
  const ours = state.query === query;
  const logs = ours && Array.isArray(state.logs) ? state.logs : [];
  const totalCount = ours ? state.totalCount : 0;
  const error = ours ? state.error : null;
  const loading = state.loading;
  const filtered = !!(applied.email || applied.action || applied.outcome || applied.from || applied.to || (!fixedUserId && applied.userId));
  const lastPage = ours ? Math.max(1, Number(state.totalPages) || 1) : 1;

  // A page past the end (e.g. after a filter change elsewhere): go to the last one.
  useEffect(() => {
    if (ours && !loading && !error && applied.page > lastPage && totalCount > 0) {
      const next = new URLSearchParams(params);
      next.set("page", String(lastPage));
      setParams(next, { replace: true });
    }
  }, [ours, loading, error, applied.page, lastPage, totalCount, params, setParams]);

  const write = (changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "") next.delete(k);
      else next.set(k, String(v));
    });
    setParams(next);
  };

  const apply = (e) => {
    e.preventDefault();
    const bad = rangeProblem(draft.from, draft.to, { required: false });
    setProblem(bad);
    if (bad) return;
    write({
      email: showEmail ? draft.email.trim() : undefined,
      action: draft.action,
      outcome: draft.outcome,
      from: draft.from,
      to: draft.to,
      page: undefined
    });
  };

  const clear = () => {
    setDraft(EMPTY);
    setProblem("");
    write({ email: undefined, action: undefined, outcome: undefined, from: undefined, to: undefined, page: undefined, userId: undefined });
  };

  const firstLoad = !ours;
  return (
    <Stack spacing={2.5}>
      <Section id="audit-filters" title="Filters">
        <Box
          component="form"
          role="search"
          aria-label={showAccount ? "Audit log" : "This account's activity"}
          noValidate
          onSubmit={apply}
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              lg: showEmail ? "minmax(0, 1.3fr) minmax(0, 1.5fr) minmax(0, 1fr) 1fr 1fr" : "minmax(0, 1.5fr) minmax(0, 1fr) 1fr 1fr"
            }
          }}
        >
          {showEmail && (
            <TextField
              id="audit-email"
              label="Account email contains"
              value={draft.email}
              onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
              autoComplete="off"
            />
          )}
          <TextField
            select
            id="audit-action"
            label="What happened"
            value={draft.action}
            onChange={(e) => setDraft((d) => ({ ...d, action: e.target.value }))}
          >
            <MenuItem value="">Everything</MenuItem>
            <ListSubheader>Kinds of event</ListSubheader>
            {AUDIT_GROUPS.map((g) => (
              <MenuItem key={g.id} value={`group:${g.id}`}>
                {g.label}
              </MenuItem>
            ))}
            <ListSubheader>One action</ListSubheader>
            {AUDIT_ACTIONS.map((a) => (
              <MenuItem key={a.value} value={a.value}>
                {a.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            id="audit-outcome"
            label="Outcome"
            value={draft.outcome}
            onChange={(e) => setDraft((d) => ({ ...d, outcome: e.target.value }))}
          >
            <MenuItem value="">Any outcome</MenuItem>
            {OUTCOMES.map((o) => (
              <MenuItem key={o} value={o}>
                {auditOutcomeLabel(o)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            id="audit-from"
            label="From"
            type="date"
            value={draft.from}
            onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
            error={!!problem}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: draft.to || undefined } }}
          />
          <TextField
            id="audit-to"
            label="To"
            type="date"
            value={draft.to}
            onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
            error={!!problem}
            helperText="Includes this day (Ontario time)"
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: draft.from || undefined } }}
          />
          {problem && (
            <Alert severity="error" role="alert" sx={{ gridColumn: "1 / -1" }}>
              {problem}
            </Alert>
          )}
          <Stack direction="row" spacing={1.5} sx={{ gridColumn: "1 / -1", flexWrap: "wrap", rowGap: 1.5 }}>
            <Button type="submit" variant="contained" startIcon={<SearchOutlinedIcon />}>
              Apply filters
            </Button>
            {filtered && (
              <Button startIcon={<FilterAltOffOutlinedIcon />} onClick={clear}>
                Clear filters
              </Button>
            )}
          </Stack>
        </Box>
      </Section>

      <Section id="audit-entries" title="Entries" contentSx={{ p: { xs: 2, sm: 3 } }}>
        {!fixedUserId && applied.userId && (
          <Typography sx={{ mb: 1, fontWeight: 600 }}>Showing one account's entries.</Typography>
        )}
        {error && logs.length === 0 && ours ? (
          <ErrorState error={error} title="We couldn't load the audit log" onRetry={load} />
        ) : firstLoad ? (
          <LoadingState variant="table" rows={6} label="Loading the audit log…" />
        ) : (
          <>
            <Typography role="status" sx={{ color: "text.secondary", mb: 1 }} data-testid="audit-count">
              {loading
                ? "Updating…"
                : `${formatNumber(totalCount)} ${totalCount === 1 ? "entry" : "entries"}${filtered ? " match your filters" : ""}, newest first`}
            </Typography>
            <Box sx={{ height: 8, mb: 1 }}>{loading && <LinearProgress aria-label="Updating the audit log" />}</Box>
            {error && <ErrorState compact error={error} onRetry={load} />}
            {logs.length === 0 ? (
              <EmptyState
                compact
                icon={ReceiptLongOutlinedIcon}
                title={filtered ? "No entries match" : "No entries yet"}
                description={
                  filtered
                    ? "Try a wider date range, another kind of event or part of the email address."
                    : "Entries appear here as students and staff use the site."
                }
              />
            ) : (
              <>
                <AuditLogTable logs={logs} label={tableLabel} showAccount={showAccount} />
                <ServerPagination
                  totalCount={totalCount}
                  page={Math.min(applied.page, lastPage)}
                  limit={applied.limit}
                  options={LIMITS}
                  onPage={(p) => write({ page: p > 1 ? p : undefined })}
                  onLimit={(l) => write({ limit: l !== LIMITS[0] ? l : undefined, page: undefined })}
                />
              </>
            )}
          </>
        )}
      </Section>
    </Stack>
  );
}
